import React, { useState } from "react";
import { Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { ArrowDown, LogOut, X } from "react-feather";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { History } from "history";
import { routes } from "../routes";
import userService from "../../services/userService";
import { connect } from "react-redux";
import { IApplicationState } from "../../redux/reducers";
import SubscriptionCheckoutModal from "../../components/checkout/subscriptionModal";
import FeatureTable from "../account/billing/components/featureTable";
import StripeClimate from "../../components/checkout/stripeClimate";
import { availableSubscriptionPlans, SUBSCRIPTION_OPTIONS } from "../../utils/constants";
import { toCamelCase } from "../../utils/string";
import SubscriptionCard from "./components/subscriptionCard";
import { SparkleIcon } from "../../assets/icons";

interface IProps extends RouteComponentProps {
	logout: () => void;
	history: History;
}

const Subscribe: React.FC<IProps> = (props) => {
	const [modal, setModal] = useState<boolean>();
	const [selectedPlan, setSelectedPlan] = useState<availableSubscriptionPlans>();

	const handleSelectPlan = (plan: availableSubscriptionPlans) => {
		setSelectedPlan(plan);
		setModal(true);
	};

	return (
		<div className="subscribe">
			<div className="log-out" onClick={props.logout}>
				Logout <LogOut size={24} />
			</div>
			<div className={"subscription-options"}>
				<Row>
					<h3 className={"mb-3"}>
						<span style={{ color: "#3f65f1" }}>Plans</span> <br /> for Every Team
					</h3>
				</Row>

				{/* Start Free Trial Section */}
				<div className="start-free-section">
					<div className="start-free-content">
						<div className="start-free-left">
							<div className="start-free-header">
								<div className="start-free-icon-circle">
									<SparkleIcon size={24} color="#10b981" />
								</div>
								<div className="start-free-text">
									<h4>Start Free</h4>
									<p className="no-credit-card">No credit card required</p>
								</div>
							</div>
							<p className="trial-description">
								Try Journey Foods risk-free for 14 days. Get access to core features with
								limited credits to explore the platform.
							</p>
							<button
								className="btn-start-trial"
								onClick={() => props.history.push(routes.DASHBOARD)}
							>
								<SparkleIcon size={16} color="#ffffff" />
								Go To Dashboard
							</button>
						</div>
						<div className="start-free-right">
							<h5>Free trial includes:</h5>
							<div className="trial-features">
								<div className="trial-feature-col">
									<div className="trial-feature-item">
										<span className="checkmark">✓</span> 50 ingredient searches
									</div>
									<div className="trial-feature-item">
										<span className="checkmark">✓</span> 20 AI recommendations
									</div>
									<div className="trial-feature-item">
										<span className="checkmark">✓</span> 500 API calls
									</div>
								</div>
								<div className="trial-feature-col">
									<div className="trial-feature-item">
										<span className="checkmark">✓</span> 10 nutrition analyses
									</div>
									<div className="trial-feature-item">
										<span className="checkmark">✓</span> 3 report exports
									</div>
									<div className="trial-feature-item">
										<span className="checkmark">✓</span> 14-day access
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Pricing Cards */}
				<div className="pricing-cards-container">
					{Object.keys(SUBSCRIPTION_OPTIONS).map((key) => {
						const plan = SUBSCRIPTION_OPTIONS[key];
						return (
							<SubscriptionCard
								key={key}
								subscriptionName={plan.subscriptionName}
								subscriptionDetails={plan.subscriptionDetails}
								subscriptionPrice={plan.subscriptionPrice}
								subscriptionInterval={plan.subscriptionInterval}
								subscriptionDescription={plan.subscriptionDescription}
								dividerColor={plan.dividerColor}
								handleSelectPlan={handleSelectPlan}
								isPopular={plan.subscriptionName === availableSubscriptionPlans.growth}
								isSelected={selectedPlan === plan.subscriptionName}
							/>
						);
					})}
				</div>

				{/* How Credits Work Section */}
				<div className="credits-section">
					<h3>How Credits Work</h3>
					<p className="credits-description">
						Each plan includes monthly credits for different platform features. Credits reset monthly and scale with
						your plan. Need more? Upgrade anytime or purchase add-ons.
					</p>
					<div className="credits-table-container">
						<div className="credits-table">
							<div className="credits-column">
								<h5>Ingredient Searches</h5>
								<div className="credit-row">
									<span className="tier-label">Fresh</span>
									<span className="credit-value">50</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Growth</span>
									<span className="credit-value">200</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Group</span>
									<span className="credit-value">500</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Enterprise</span>
									<span className="credit-value unlimited">Unlimited</span>
								</div>
							</div>
							<div className="credits-column">
								<h5>Nutrition Analyses</h5>
								<div className="credit-row">
									<span className="tier-label">Fresh</span>
									<span className="credit-value">10</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Growth</span>
									<span className="credit-value">50</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Group</span>
									<span className="credit-value">100</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Enterprise</span>
									<span className="credit-value unlimited">Unlimited</span>
								</div>
							</div>
							<div className="credits-column">
								<h5>AI Recommendations</h5>
								<div className="credit-row">
									<span className="tier-label">Fresh</span>
									<span className="credit-value">20</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Growth</span>
									<span className="credit-value">100</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Group</span>
									<span className="credit-value">250</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Enterprise</span>
									<span className="credit-value unlimited">Unlimited</span>
								</div>
							</div>
							<div className="credits-column">
								<h5>Report Exports</h5>
								<div className="credit-row">
									<span className="tier-label">Fresh</span>
									<span className="credit-value">3</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Growth</span>
									<span className="credit-value">10</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Group</span>
									<span className="credit-value">25</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Enterprise</span>
									<span className="credit-value unlimited">Unlimited</span>
								</div>
							</div>
							<div className="credits-column">
								<h5>API Calls</h5>
								<div className="credit-row">
									<span className="tier-label">Fresh</span>
									<span className="credit-value">500</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Growth</span>
									<span className="credit-value">2000</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Group</span>
									<span className="credit-value">5,000</span>
								</div>
								<div className="credit-row">
									<span className="tier-label">Enterprise</span>
									<span className="credit-value unlimited">Unlimited</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className={"d-flex justify-content-center mt-5"}>
					<div className="view-features">
						<a href="#features">View All Features</a>
						<ArrowDown />
					</div>
				</div>
				<div className="notice">
					If you have already purchased a subscription directly through one of our team members,
					please contact us at <a href="mailto:hello@journeyfoods.com">hello@journeyfoods.com</a> to
					make sure that you have access to the platform.
				</div>
			</div>
			{/* CPG Features */}
			<div
				className={"mt-4"}
				id="features"
				style={{ padding: "0 40px", display: "flex", flexDirection: "column", width: "100%", maxWidth: "1200px" }}
			>
				<h3 style={{ alignSelf: "center", margin: "0.5rem" }}>All Features</h3>
				<FeatureTable />
			</div>

			{/* Supplier Features */}
			<div
				className={"mt-4"}
				id="supplier-features"
				style={{ padding: "0 40px", display: "flex", flexDirection: "column", width: "100%", maxWidth: "1200px" }}
			>
				<h3 className={"mb-3"} style={{ alignSelf: "center", margin: "0.5rem" }}>
					Journey Foods for Suppliers
				</h3>
				<FeatureTable supplier />
			</div>
			<div className={"d-flex align-item-center justify-content-center pb-3"}>
				<div style={{ width: 500 }}>
					<StripeClimate />
				</div>
			</div>
			{selectedPlan && (
				<Modal className="subscription-modal" isOpen={modal}>
					<ModalHeader>
						<div className={"modal-header-content"}>
							<span>{`Subscribe to ${toCamelCase(selectedPlan)} plan`}</span>
							<X
								onClick={() => {
									setModal(false);
								}}
								className={"close-modal"}
								size={24}
							></X>
						</div>
					</ModalHeader>
					<ModalBody>
						<SubscriptionCheckoutModal subscriptionPlan={selectedPlan} />
					</ModalBody>
				</Modal>
			)}
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({});

const mapDispatchToProps = {
	logout: () => userService.logout()
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Subscribe));
